import { AnimatePresence, motion } from 'motion/react';
import { Undo2, X } from 'lucide-react';

import { cn } from '@/lib/cn';
import { motionSafe, springExit, springSheet } from '@/lib/motion';
import { useToastStore } from '@/lib/toast';

const TONE: Record<string, string> = {
  neutral: 'text-text',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
};

export function Toaster() {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);

  return (
    <div
      // aria-live so the message is announced without stealing focus. A toast
      // that grabs focus interrupts typing, which is the opposite of the point.
      aria-live="polite"
      aria-atomic="false"
      className={cn(
        'pointer-events-none fixed inset-x-0 bottom-0 z-toast flex flex-col items-center gap-2 p-4',
        // Clear of the focus/work-block bar and the iOS home indicator.
        'pb-[max(1rem,calc(var(--chrome-bottom,0px)+0.5rem))]',
      )}
    >
      <AnimatePresence initial={false}>
        {toasts.map((t) => (
          <motion.div
            key={t.id}
            layout
            initial={{ opacity: 0, y: 12, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={motionSafe(t.onUndo ? springSheet : springExit)}
            className={cn(
              'material pointer-events-auto flex w-full max-w-md items-center gap-3',
              'rounded-lg border px-4 py-3 shadow-lifted',
            )}
          >
            <p className={cn('min-w-0 flex-1 text-small', TONE[t.tone])}>{t.message}</p>

            {t.onUndo && (
              <button
                type="button"
                onClick={() => {
                  t.onUndo?.();
                  dismiss(t.id);
                }}
                className={cn(
                  'pressable inline-flex shrink-0 items-center gap-1.5 rounded px-2 py-1',
                  'text-small font-medium text-accent hover:bg-accent-quiet',
                )}
              >
                <Undo2 size={13} strokeWidth={1.5} aria-hidden />
                Undo
              </button>
            )}

            <button
              type="button"
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss"
              className="shrink-0 rounded p-1 text-text-tertiary hover:text-text"
            >
              <X size={14} strokeWidth={1.5} aria-hidden />
            </button>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
